import { AppRoutesList, UserRoleEnum } from '@qse/constants'
import { useUserStore } from '@qse/stores'
import { detectCurrentApp, getDefaultRoute, showAccessDeniedPopup } from '@qse/utils'
import { ReactNode, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

interface BillingGuardProps {
  children: ReactNode
}

/**
 * BillingGuard component that handles billing and registration restrictions
 * - Prevents inactive users from navigating away from billing pages
 * - Blocks TENANT_ADMIN users with incomplete/suspended/cancelled status from dashboard app
 * - Redirects users to appropriate billing or admin pages based on their status
 * Exception: SUPER_ADMIN users bypass billing restrictions completely
 */
export const BillingGuard = ({ children }: BillingGuardProps) => {
  const { user } = useUserStore()
  const location = useLocation()
  const navigate = useNavigate()
  const appDetection = detectCurrentApp()

  // Check if user should be blocked from dashboard
  const isBlockedFromDashboard =
    user?.userRole === UserRoleEnum.TENANT_ADMIN &&
    appDetection.isDashboard &&
    user.tenant?.status &&
    ['incomplete', 'suspended', 'cancelled'].includes(user.tenant.status)

  useEffect(() => {
    // Only apply billing restrictions if we have user data
    if (!user) {
      return
    }

    // SUPER_ADMIN users bypass billing restrictions completely
    if (user.userRole === UserRoleEnum.SUPER_ADMIN) {
      console.log(
        `BillingGuard: SUPER_ADMIN user bypassing billing restrictions: ${location.pathname}`,
      )

      return
    }

    if (isBlockedFromDashboard) {
      // Show access denied modal with incomplete registration reason
      showAccessDeniedPopup({
        userRole: user.userRole,
        currentApp: 'dashboard',
        targetApp: 'admin',
        reason: 'incomplete_registration',
      })

      return
    }

    // If user is inactive (billing required) and tries to access non-billing routes
    if (user.tenant?.status === 'incomplete') {
      const allowedPaths = [
        AppRoutesList.billing,
        AppRoutesList.success,
        AppRoutesList.error,
        AppRoutesList.logout,
      ]

      const isOnAllowedPath = allowedPaths.some(path => {
        // Handle parameterized routes like '/dashboard/management/tenant/:id'
        if (path.includes(':')) {
          const pathPattern = path.replace(/:[^/]+/g, '[^/]+')
          const regex = new RegExp(`^${pathPattern}$`)

          return regex.test(location.pathname)
        }

        // Handle trailing slash variations
        const normalizedPathname = location.pathname.replace(/\/$/, '') // Remove trailing slash
        const normalizedPath = path.replace(/\/$/, '') // Remove trailing slash from path

        return normalizedPathname === normalizedPath
      })

      if (!isOnAllowedPath) {
        console.log(
          `BillingGuard: Inactive user attempting to access restricted route: ${location.pathname}, redirecting to billing`,
        )

        // Use replace to prevent back navigation to restricted routes
        navigate(AppRoutesList.billing, { replace: true })

        return
      }

      console.log(`BillingGuard: Inactive user accessing allowed route: ${location.pathname}`)
    } else if (user.tenant?.status === 'active') {
      // If user is active (subscribed), prevent access to billing pages since they don't need them
      const restrictedPathsForActiveUsers = [AppRoutesList.billing]

      const isOnRestrictedPath = restrictedPathsForActiveUsers.some(path => {
        // Handle parameterized routes
        if (path.includes(':')) {
          const pathPattern = path.replace(/:[^/]+/g, '[^/]+')
          const regex = new RegExp(`^${pathPattern}$`)

          return regex.test(location.pathname)
        }

        // Handle trailing slash variations
        const normalizedPathname = location.pathname.replace(/\/$/, '') // Remove trailing slash
        const normalizedPath = path.replace(/\/$/, '') // Remove trailing slash from path

        return normalizedPathname === normalizedPath
      })

      // Special handling for billing/cancel paths - redirect to management subscription
      if (location.pathname.includes('billing/cancel')) {
        navigate(AppRoutesList.managementSubscription, { replace: true })

        return
      }

      if (isOnRestrictedPath) {
        console.log(
          `BillingGuard: Active user attempting to access billing pages: ${location.pathname}, redirecting to dashboard`,
        )

        // Redirect active users away from billing pages to appropriate dashboard
        const defaultRoute = getDefaultRoute(user.userRole, appDetection.appType)

        navigate(defaultRoute, { replace: true })

        return
      }

      console.log(`BillingGuard: Active user accessing allowed route: ${location.pathname}`)
    }
  }, [
    user,
    location.pathname,
    navigate,
    appDetection.appType,
    appDetection.isDashboard,
    isBlockedFromDashboard,
  ])

  // Don't render children if user is blocked from dashboard
  if (isBlockedFromDashboard) {
    return null
  }

  return <>{children}</>
}
